import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emisorApi, type EmisorBody } from '../emisor';
import { CERT_BLOCKING, type Emisor } from '../types';
import { qk } from './keys';

export function useEmisor() {
  return useQuery({ queryKey: qk.emisor, queryFn: ({ signal }) => emisorApi.get(signal), staleTime: 60_000 });
}

export function useEmisorMetadata() {
  return useQuery({ queryKey: qk.emisorMeta, queryFn: ({ signal }) => emisorApi.metadata(signal), staleTime: 5 * 60_000 });
}

export function useInvalidateEmisor() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: qk.emisor }), qc.invalidateQueries({ queryKey: qk.emisorMeta })]);
}

export function useSaveEmisor() {
  const inv = useInvalidateEmisor();
  return useMutation({ mutationFn: (body: EmisorBody) => emisorApi.put(body), onSettled: () => inv() });
}

export function usePatchEmisor() {
  const inv = useInvalidateEmisor();
  return useMutation({ mutationFn: (body: Parameters<typeof emisorApi.patch>[0]) => emisorApi.patch(body), onSettled: () => inv() });
}

/** 3-step .p12 upload: presign → PUT bytes (no headers) → confirm with password. */
export function useUploadCertificate() {
  const inv = useInvalidateEmisor();
  return useMutation({
    mutationFn: async ({ ruc, file, password }: { ruc: string; file: File; password: string }) => {
      const bytes = await file.arrayBuffer();
      const { uploadUrl } = await emisorApi.certificateUploadUrl(ruc);
      if (!uploadUrl) throw new Error('El servidor no devolvió una URL de carga.');
      await emisorApi.uploadToS3(uploadUrl, bytes);
      return emisorApi.certificateConfirm(ruc, password);
    },
    onSettled: () => inv(),
  });
}

export function useUploadLogo() {
  const inv = useInvalidateEmisor();
  return useMutation({
    mutationFn: async ({ ruc, file }: { ruc: string; file: File }) => {
      const { uploadUrl, contentType, maxBytes } = await emisorApi.logoUploadUrl(ruc);
      if (file.size > maxBytes) throw new Error('El logo supera el tamaño máximo (2 MB).');
      await emisorApi.uploadToS3(uploadUrl, await file.arrayBuffer(), contentType ?? file.type);
      return emisorApi.logoConfirm(ruc);
    },
    onSettled: () => inv(),
  });
}

export function useEstablecimientoMutations() {
  const inv = useInvalidateEmisor();
  const create = useMutation({ mutationFn: emisorApi.createEstablecimiento, onSettled: () => inv() });
  const update = useMutation({
    mutationFn: (a: { id: string; body: Parameters<typeof emisorApi.updateEstablecimiento>[1] }) => emisorApi.updateEstablecimiento(a.id, a.body),
    onSettled: () => inv(),
  });
  const remove = useMutation({ mutationFn: emisorApi.deleteEstablecimiento, onSettled: () => inv() });
  const createPunto = useMutation({
    mutationFn: (a: { establecimientoId: string; body: Parameters<typeof emisorApi.createPunto>[1] }) => emisorApi.createPunto(a.establecimientoId, a.body),
    onSettled: () => inv(),
  });
  const updatePunto = useMutation({
    mutationFn: (a: { id: string; body: Parameters<typeof emisorApi.updatePunto>[1] }) => emisorApi.updatePunto(a.id, a.body),
    onSettled: () => inv(),
  });
  const removePunto = useMutation({ mutationFn: emisorApi.deletePunto, onSettled: () => inv() });
  return { create, update, remove, createPunto, updatePunto, removePunto };
}

/** Derived readiness flags used by the emit form. */
export function emisorReadiness(e: Emisor | null | undefined) {
  const certificateReady = !!e?.isSignatureConfigured;
  const certificateBlocksEmit = !!e && CERT_BLOCKING.includes(e.validationStatus);
  const emisorReady = !!e?.ruc && !!e?.razonSocial;
  return { certificateReady, certificateBlocksEmit, emisorReady };
}
